import { createErrorContext, type ErrorContext } from './agentMessageValidator';

/**
 * Log levels for error reporting
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  CRITICAL = 'critical'
}

/**
 * Structured log entry interface
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
  context: ErrorContext;
  data?: any;
  stack?: string;
  tags?: string[];
}

/**
 * Error logger configuration
 */
export interface ErrorLoggerConfig {
  enableConsoleLogging: boolean;
  enableStructuredLogging: boolean;
  maxLogEntries: number;
  logLevel: LogLevel;
  includeStackTrace: boolean;
  includeUserAgent: boolean;
  includeUrl: boolean;
  enablePerformanceMetrics: boolean;
}

/**
 * Default configuration for the error logger
 */
const DEFAULT_CONFIG: ErrorLoggerConfig = {
  enableConsoleLogging: true,
  enableStructuredLogging: true,
  maxLogEntries: 1000,
  logLevel: LogLevel.WARN,
  includeStackTrace: true,
  includeUserAgent: true,
  includeUrl: true,
  enablePerformanceMetrics: false
};

/**
 * Performance metrics for error tracking
 */
interface PerformanceMetrics {
  errorCount: number;
  warningCount: number;
  lastErrorTime: number;
  averageErrorInterval: number;
  errorsByComponent: Record<string, number>;
  errorsByFunction: Record<string, number>;
}

/**
 * Comprehensive error logger utility class with structured logging and monitoring
 * 
 * Features:
 * - Structured logging with context information
 * - Multiple log levels with filtering
 * - Performance metrics and error tracking
 * - Browser console integration with proper formatting
 * - Memory-efficient log storage with rotation
 * - Error context collection and reporting
 */
export class ErrorLogger {
  private static instance: ErrorLogger;
  private config: ErrorLoggerConfig;
  private logEntries: LogEntry[] = [];
  private performanceMetrics: PerformanceMetrics;
  private logLevelPriority: Record<LogLevel, number> = {
    [LogLevel.DEBUG]: 0,
    [LogLevel.INFO]: 1,
    [LogLevel.WARN]: 2,
    [LogLevel.ERROR]: 3,
    [LogLevel.CRITICAL]: 4
  };

  /**
   * Private constructor for singleton pattern
   */
  private constructor(config: Partial<ErrorLoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.performanceMetrics = {
      errorCount: 0,
      warningCount: 0,
      lastErrorTime: 0,
      averageErrorInterval: 0,
      errorsByComponent: {},
      errorsByFunction: {}
    };

    // Initialize global error handlers
    this.initializeGlobalErrorHandlers();
  }

  /**
   * Get singleton instance
   */
  static getInstance(config?: Partial<ErrorLoggerConfig>): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger(config);
    }
    return ErrorLogger.instance;
  }

  /**
   * Initialize global error handlers for unhandled errors
   */
  private initializeGlobalErrorHandlers(): void {
    if (typeof window !== 'undefined') {
      // Handle unhandled JavaScript errors
      window.addEventListener('error', (event) => {
        this.logError(
          'GlobalErrorHandler',
          event.error || new Error(event.message),
          {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            type: 'unhandled-error'
          }
        );
      });

      // Handle unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        this.logError(
          'GlobalPromiseRejectionHandler',
          event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
          {
            type: 'unhandled-promise-rejection',
            reason: event.reason
          }
        );
      });
    }
  }

  /**
   * Update logger configuration
   */
  updateConfig(newConfig: Partial<ErrorLoggerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Check if a log level should be logged based on current configuration
   */
  private shouldLog(level: LogLevel): boolean {
    return this.logLevelPriority[level] >= this.logLevelPriority[this.config.logLevel];
  }

  /**
   * Create a structured log entry
   */
  private createLogEntry(
    level: LogLevel,
    component: string,
    functionName: string,
    message: string,
    data?: any,
    error?: Error,
    tags?: string[]
  ): LogEntry {
    const context = createErrorContext(component, functionName, {
      originalData: data
    });

    const logEntry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      context,
      data,
      tags: tags || []
    };

    if (error && this.config.includeStackTrace) {
      logEntry.stack = error.stack;
    }

    return logEntry;
  }

  /**
   * Add log entry to storage with rotation
   */
  private addLogEntry(entry: LogEntry): void {
    if (!this.config.enableStructuredLogging) {
      return;
    }

    this.logEntries.push(entry);

    // Rotate logs if we exceed max entries
    if (this.logEntries.length > this.config.maxLogEntries) {
      this.logEntries = this.logEntries.slice(-this.config.maxLogEntries);
    }

    // Update performance metrics
    this.updatePerformanceMetrics(entry);
  }

  /**
   * Update performance metrics based on log entry
   */
  private updatePerformanceMetrics(entry: LogEntry): void {
    if (!this.config.enablePerformanceMetrics) {
      return;
    }

    const { level, context } = entry;

    if (level === LogLevel.ERROR || level === LogLevel.CRITICAL) {
      this.performanceMetrics.errorCount++;
      
      // Calculate average error interval
      if (this.performanceMetrics.lastErrorTime > 0) {
        const interval = entry.timestamp - this.performanceMetrics.lastErrorTime;
        this.performanceMetrics.averageErrorInterval = 
          (this.performanceMetrics.averageErrorInterval + interval) / 2;
      }
      this.performanceMetrics.lastErrorTime = entry.timestamp;

      // Track errors by component
      const component = context.component;
      this.performanceMetrics.errorsByComponent[component] = 
        (this.performanceMetrics.errorsByComponent[component] || 0) + 1;

      // Track errors by function
      const functionKey = `${component}.${context.function}`;
      this.performanceMetrics.errorsByFunction[functionKey] = 
        (this.performanceMetrics.errorsByFunction[functionKey] || 0) + 1;
    }

    if (level === LogLevel.WARN) {
      this.performanceMetrics.warningCount++;
    }
  }

  /**
   * Format log entry for console output
   */
  private formatConsoleOutput(entry: LogEntry): void {
    if (!this.config.enableConsoleLogging) {
      return;
    }

    const { level, message, context, timestamp, stack, data } = entry;
    const timeStr = new Date(timestamp).toISOString();
    const componentStr = `[${context.component}.${context.function}]`;

    // Choose appropriate console method based on log level
    const consoleMethod = level === LogLevel.ERROR || level === LogLevel.CRITICAL ? 'error' :
                         level === LogLevel.WARN ? 'warn' :
                         level === LogLevel.INFO ? 'info' : 'log';

    // Create styled console output
    const styles = {
      [LogLevel.DEBUG]: 'color: #888; font-size: 11px;',
      [LogLevel.INFO]: 'color: #0066cc; font-weight: normal;',
      [LogLevel.WARN]: 'color: #ff8800; font-weight: bold;',
      [LogLevel.ERROR]: 'color: #cc0000; font-weight: bold;',
      [LogLevel.CRITICAL]: 'color: #ffffff; background-color: #cc0000; font-weight: bold; padding: 2px 4px;'
    };

    // Log main message
    console[consoleMethod](
      `%c${level.toUpperCase()} %c${timeStr} %c${componentStr} %c${message}`,
      styles[level],
      'color: #666; font-size: 11px;',
      'color: #0066cc; font-weight: bold;',
      'color: inherit; font-weight: normal;'
    );

    // Log additional data if present
    if (data) {
      console.groupCollapsed('📊 Additional Data');
      console.table(data);
      console.groupEnd();
    }

    // Log context information
    if (context.agentName || context.agentType || context.originalData) {
      console.groupCollapsed('🔍 Context Information');
      console.table({
        'Agent Name': context.agentName || 'N/A',
        'Agent Type': context.agentType || 'N/A',
        'User Agent': context.userAgent,
        'URL': context.url,
        'Timestamp': new Date(context.timestamp).toISOString()
      });
      if (context.originalData) {
        console.log('Original Data:', context.originalData);
      }
      console.groupEnd();
    }

    // Log stack trace if available
    if (stack && (level === LogLevel.ERROR || level === LogLevel.CRITICAL)) {
      console.groupCollapsed('📋 Stack Trace');
      console.error(stack);
      console.groupEnd();
    }
  }

  /**
   * Log an error with comprehensive context information
   */
  logError(
    component: string,
    error: Error | string,
    additionalData?: any,
    functionName: string = 'unknown',
    tags?: string[]
  ): void {
    if (!this.shouldLog(LogLevel.ERROR)) {
      return;
    }

    const errorObj = error instanceof Error ? error : new Error(String(error));
    const message = `Error in ${component}: ${errorObj.message}`;

    const entry = this.createLogEntry(
      LogLevel.ERROR,
      component,
      functionName,
      message,
      additionalData,
      errorObj,
      tags
    );

    this.addLogEntry(entry);
    this.formatConsoleOutput(entry);
  }

  /**
   * Log a warning with context information
   */
  logWarning(
    component: string,
    message: string,
    additionalData?: any,
    functionName: string = 'unknown',
    tags?: string[]
  ): void {
    if (!this.shouldLog(LogLevel.WARN)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.WARN,
      component,
      functionName,
      message,
      additionalData,
      undefined,
      tags
    );

    this.addLogEntry(entry);
    this.formatConsoleOutput(entry);
  }

  /**
   * Log an info message
   */
  logInfo(
    component: string,
    message: string,
    additionalData?: any,
    functionName: string = 'unknown',
    tags?: string[]
  ): void {
    if (!this.shouldLog(LogLevel.INFO)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.INFO,
      component,
      functionName,
      message,
      additionalData,
      undefined,
      tags
    );

    this.addLogEntry(entry);
    this.formatConsoleOutput(entry);
  }

  /**
   * Log a debug message
   */
  logDebug(
    component: string,
    message: string,
    additionalData?: any,
    functionName: string = 'unknown',
    tags?: string[]
  ): void {
    if (!this.shouldLog(LogLevel.DEBUG)) {
      return;
    }

    const entry = this.createLogEntry(
      LogLevel.DEBUG,
      component,
      functionName,
      message,
      additionalData,
      undefined,
      tags
    );

    this.addLogEntry(entry);
    this.formatConsoleOutput(entry);
  }

  /**
   * Log a critical error (highest priority)
   */
  logCritical(
    component: string,
    error: Error | string,
    additionalData?: any,
    functionName: string = 'unknown',
    tags?: string[]
  ): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const message = `CRITICAL ERROR in ${component}: ${errorObj.message}`;

    const entry = this.createLogEntry(
      LogLevel.CRITICAL,
      component,
      functionName,
      message,
      additionalData,
      errorObj,
      tags
    );

    this.addLogEntry(entry);
    this.formatConsoleOutput(entry);

    // Always log critical errors regardless of configuration
    console.error('🚨 CRITICAL ERROR 🚨', entry);
  }

  /**
   * Get all log entries
   */
  getLogEntries(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logEntries.filter(entry => entry.level === level);
    }
    return [...this.logEntries];
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetrics {
    return { ...this.performanceMetrics };
  }

  /**
   * Clear all log entries
   */
  clearLogs(): void {
    this.logEntries = [];
    this.performanceMetrics = {
      errorCount: 0,
      warningCount: 0,
      lastErrorTime: 0,
      averageErrorInterval: 0,
      errorsByComponent: {},
      errorsByFunction: {}
    };
  }

  /**
   * Export logs as JSON for external analysis
   */
  exportLogs(): string {
    return JSON.stringify({
      config: this.config,
      logs: this.logEntries,
      metrics: this.performanceMetrics,
      exportTime: Date.now()
    }, null, 2);
  }

  /**
   * Get error summary for debugging
   */
  getErrorSummary(): {
    totalErrors: number;
    totalWarnings: number;
    recentErrors: LogEntry[];
    topErrorComponents: Array<{ component: string; count: number }>;
    topErrorFunctions: Array<{ function: string; count: number }>;
  } {
    const recentErrors = this.logEntries
      .filter(entry => entry.level === LogLevel.ERROR || entry.level === LogLevel.CRITICAL)
      .slice(-10);

    const topErrorComponents = Object.entries(this.performanceMetrics.errorsByComponent)
      .map(([component, count]) => ({ component, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const topErrorFunctions = Object.entries(this.performanceMetrics.errorsByFunction)
      .map(([func, count]) => ({ function: func, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalErrors: this.performanceMetrics.errorCount,
      totalWarnings: this.performanceMetrics.warningCount,
      recentErrors,
      topErrorComponents,
      topErrorFunctions
    };
  }
}

/**
 * Convenience function to get the default error logger instance
 */
export const getErrorLogger = (config?: Partial<ErrorLoggerConfig>): ErrorLogger => {
  return ErrorLogger.getInstance(config);
};

/**
 * Convenience functions for common logging operations
 */
export const logError = (component: string, error: Error | string, data?: any, functionName?: string) => {
  getErrorLogger().logError(component, error, data, functionName);
};

export const logWarning = (component: string, message: string, data?: any, functionName?: string) => {
  getErrorLogger().logWarning(component, message, data, functionName);
};

export const logInfo = (component: string, message: string, data?: any, functionName?: string) => {
  getErrorLogger().logInfo(component, message, data, functionName);
};

export const logDebug = (component: string, message: string, data?: any, functionName?: string) => {
  getErrorLogger().logDebug(component, message, data, functionName);
};

export const logCritical = (component: string, error: Error | string, data?: any, functionName?: string) => {
  getErrorLogger().logCritical(component, error, data, functionName);
};

export default ErrorLogger;